<template>
   <div class="start-page-bg">
      <div class="container">
         <div class="row align-items-center justify-content-center vh-100">
            <div class="card col-xl-5 col-md-8 col-lg-6 col-sm-12">

               <div class="card-body">

                  <div class="text-center" style="position:relative;">
                     <img src="@/assets/img/spark_logo_black.svg" alt="spark_logo" class="img-fluid w-25" />
                     <button type="button" class="btn btn-sm btn-info text-light" style="position:absolute;right:0"
                        @click="login()"> Login to Spark </button>
                  </div>

                  <div class="text-center justify-content-center">
                     <h2 class="card-title"><strong>Lead</strong></h2>
                  </div>

                  <hr />

                  <div class="row m-3 text-center">
                     <h6 class="card-subtitle text-muted">Date: <strong>{{ this.currentDate }}</strong></h6>
                  </div>

                  <form class="row mx-4 g-0" style="gap: 0.5rem">

                     <div class="form-floating">
                        <input type="text" id="organisation" v-model.trim="lead.organisation"
                           @blur="v$.lead.organisation.$touch" class="form-control" placeholder="Organisation Name" />
                        <label for="organisation" class="text-muted">Organisation Name <span class="text-danger">*</span></label>
                        <span class="form-text text-danger" v-if="v$.lead.organisation.$error">
                           Organisation is required.
                        </span>
                     </div>

                     <div>
                        <VueMultiselect v-model="lead.selectedCountry" :options="countryOptions" :multiple="false"
                           :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                           placeholder="Select a country" label="name" track-by="code" :preselect-first="true">
                           <template #option="{ option }">
                              <div class="option-with-flag">
                                 <span class="flag">{{ option.flag }}</span>
                                 &nbsp;
                                 <span>{{ option.name }}</span>
                              </div>
                           </template>

                           <template #singleLabel="{ option }">
                              <div class="option-with-flag">
                                 <span class="flag">{{ option.flag }}</span>
                                 &nbsp;
                                 <span>{{ option.name }}</span>
                              </div>
                           </template>
                        </VueMultiselect>
                     </div>

                     <div class="form-floating">
                        <input type="text" id="firstName" v-model.trim="lead.firstName" @blur="v$.lead.firstName.$touch"
                           class="form-control" placeholder="First Name" />
                        <label for="firstName" class="text-muted">First Name <span class="text-danger">*</span></label>
                        <span class="form-text text-danger" v-if="v$.lead.firstName.$error">
                           First Name is required.
                        </span>
                     </div>

                     <div class="form-floating">
                        <input type="text" id="lastName" v-model.trim="lead.lastName" @blur="v$.lead.lastName.$touch"
                           class="form-control" placeholder="Last Name" />
                        <label for="lastName" class="text-muted">Last Name <span class="text-danger">*</span></label>
                        <span class="form-text text-danger" v-if="v$.lead.lastName.$error">
                           Last Name is required.
                        </span>
                     </div>

                     <div class="form-floating">
                        <input type="email" id="email" v-model.trim="lead.email" @blur="v$.lead.email.$touch"
                           class="form-control" placeholder="Email" />
                        <label for="email" class="text-muted">Email <span class="text-danger">*</span></label>

                        <span class="form-text text-danger error-msg"
                           v-if="v$.lead.email.$error && v$.lead.email.required.$invalid">
                           Email is required.
                        </span>
                        <span class="form-text text-danger error-msg"
                           v-if="v$.lead.email.$error && v$.lead.email.email.$invalid">
                           Must be a valid email.
                        </span>
                     </div>

                     <vue-tel-input v-model.trim="lead.contactNumber" v-bind="vueTel.props"
                        mode="international"></vue-tel-input>

                     <div class="form-floating">
                        <input type="text" id="abn" v-model.trim="lead.abn" @blur="v$.lead.abn.$touch"
                           class="form-control" :placeholder="businessNumberPlaceholder" />
                        <label for="abn" class="text-muted">{{ businessNumberLabel }}</label>
                        <span class="form-text text-danger" v-if="v$.lead.abn.$error">
                           Invalid {{ businessNumberShortLabel }} format.
                        </span>
                     </div>

                     <div class="form-check align-items-center">
                        <input class="form-check-input" type="checkbox" :value="true" id="isCharity"
                           v-model.trim="lead.isCharity">
                        <label class="form-check-label pt-1" for="isCharity">
                           <strong>Is a registered charity?</strong>
                        </label>
                     </div>

                     <div class="g-0 d-row d-flex flex-wrap flex-md-nowrap" style="gap: 0.5rem">

                        <div class="form-floating w-50 vw-100">
                           <select class="form-select" id="leadType" v-model.trim="lead.leadType"
                              @blur="v$.lead.leadType.$touch" @change="v$.lead.leadType.$touch">
                              <option selected disabled value>Please choose...</option>
                              <option v-for="leadType in leadTypes" :value="leadType.id" :key="leadType.id">
                                 {{ leadType.display_name }}</option>
                           </select>
                           <label for="leadType" class="text-muted">Lead Type <span class="text-danger">*</span></label>
                           <span class="form-text text-danger" v-if="v$.lead.leadType.$dirty && lead.leadType == ''">
                              Lead Type is required.
                           </span>
                        </div>

                        <div class="form-floating w-50 vw-100">
                           <select class="form-select" id="priority" v-model.trim="lead.priority"
                              @blur="v$.lead.priority.$touch" @change="v$.lead.priority.$touch">
                              <option selected disabled value>Please choose...</option>
                              <option v-for="leadPriorityType in leadPriorityTypes" :value="leadPriorityType.id"
                                 :key="leadPriorityType.id">
                                 {{ leadPriorityType.display_name }}</option>
                           </select>
                           <label for="priority" class="text-muted">Priority <span class="text-danger">*</span></label>
                           <span class="form-text text-danger" v-if="v$.lead.priority.$dirty && lead.priority == ''">
                              Priority is required.
                           </span>
                        </div>

                     </div>

                     <div class="form-floating">
                        <VueMultiselect id="products" v-model="lead.products" :options="productList" :multiple="true"
                           :close-on-select="true" label="display_name" track-by="id" :searchable="false"
                           placeholder="Please choose at least one...">
                        </VueMultiselect>
                        <label for="products" class="text-muted">Products <span class="text-danger">*</span></label>
                        <!-- <span class="form-text text-danger">
                           Please choose at least one.
                        </span> -->
                     </div>

                     <div class="form-floating">
                        <select class="form-select" id="marketingChannel" v-model="lead.marketing_channel">
                           <option selected disabled value>Please choose...</option>
                           <option v-for="marketingChannel in marketingChannels" :value="marketingChannel.id"
                              :key="marketingChannel.id">
                              {{ marketingChannel.display_name }}</option>
                        </select>
                        <label for="marketingChannel" class="form-label">(Optional) How did you find Vertex?</label>
                     </div>

                     <div class="form-floating">
                        <textarea class="form-control" id="enquiryDetails" v-model.trim="lead.enquiryDetails"
                           @blur="v$.lead.enquiryDetails.$touch" placeholder="Enquiry Details"
                           style="height: 150px"></textarea>
                        <label for="enquiryDetails" class="text-muted">Enquiry Details <span class="text-danger">*</span></label>
                        <span class="form-text text-danger" v-if="v$.lead.enquiryDetails.$error">
                           Enquiry Details is required.
                        </span>
                     </div>

                     <div class="pt-3 pb-3 justify-content-center d-flex" style="gap:0.5rem">
                        <button type="button" class="btn btn-outline-warning"
                           @click="resetForm(); setDefaultCountryCode();">Clear
                           fields</button>
                        <button type="button" class="btn btn-info text-light" @click="createLead()"
                           :disabled="disableFormButton()"> Create
                        </button>
                     </div>
                  </form>


               </div>

            </div>
         </div>
      </div>
   </div>

</template>

<script>
import moment from "moment";
import { useVuelidate } from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import "@/assets/scss/vue-multiselect.scss";
import { countries } from 'country-data';
import {
   isAustralianCountry,
   getBusinessNumberLabel,
   getBusinessNumberShortLabel,
   getBusinessNumberPlaceholder,
   validateBusinessNumber,
} from '@/plugins/businessValidator.js';

export default {

   components: {
      VueTelInput,
      VueMultiselect,
   },

   setup() {
      return { v$: useVuelidate() }
   },

   data() {
      const countryOptions = countries.all.map(country => ({
         code: country.alpha2,
         name: country.name,
         flag: country.emoji,
      }));

      // Find Australia in the countryOptions array
      const preselectedCountry = countryOptions.find(country => country.code === 'AU');


      return {
         currentDate: null,
         lead: {
            organisation: '',
            firstName: '',
            lastName: '',
            email: '',
            contactNumber: '',
            abn: '',
            isCharity: false,
            leadType: '',
            priority: '',
            products: [],
            enquiryDetails: '',
            marketing_channel: '',
            selectedCountry: preselectedCountry,
         },
         countryOptions: countryOptions,
         leadTypes: [],
         leadPriorityTypes: [],
         marketingChannels: [],
         productList: [],
         vueTel: {
            props: {
               preferredCountries: ['AU', 'US'],
               defaultCountry: "AU",
               validCharactersOnly: true,
               dropdownOptions: {
                  // showDialCodeInSelection: true,
                  showDialCodeInList: true,
                  showFlags: true,
                  showSearchBox: true,
               },
               inputOptions: {
                  autocomplete: "on",
                  showDialCode: true,
                  placeholder: "Contact Number",
                  styleClasses: "form-control",
               },
            }
         },
      }
   },

   validations() {
      return {
         lead: {
            organisation: { required },
            firstName: { required },
            lastName: { required },
            email: { required, email },
            abn: {
               validBusinessNumber: (value) => {
                  const countryCode = this.lead.selectedCountry?.code || 'AU';
                  return validateBusinessNumber(value, countryCode);
               }
            },
            leadType: { required },
            priority: { required },
            enquiryDetails: { required },
         },
      }
   },

   computed: {
      leadCountryCode() {
         return this.lead.selectedCountry?.code || 'AU';
      },
      isAustralianLead() {
         return isAustralianCountry(this.leadCountryCode);
      },
      businessNumberLabel() {
         return getBusinessNumberLabel(this.leadCountryCode);
      },
      businessNumberShortLabel() {
         return getBusinessNumberShortLabel(this.leadCountryCode);
      },
      businessNumberPlaceholder() {
         return getBusinessNumberPlaceholder(this.leadCountryCode);
      },
   },

   created() {
      this.init()
   },

   methods: {
      init() {
         this.currentDate = moment(new Date()).format("DD-MMM-YYYY");
         this.getLeadTypeList()
         this.getLeadPriorityList()
         this.getProductList()
         this.getMarketingChannelList()
      },

      resetForm() {
         this.v$.$reset()
         this.lead.organisation = ""
         this.lead.firstName = ""
         this.lead.lastName = ""
         this.lead.email = ""
         this.lead.abn = ""
         this.lead.isCharity = false
         this.lead.leadType = ""
         this.lead.leadStatus = ""
         this.lead.priority = ""
         this.lead.products = []
         this.lead.enquiryDetails = ""
         this.lead.marketing_channel = ""
         this.setDefaultCountryCode()
      },

      setDefaultCountryCode() {
         // For Contact Number field - Change back to AU flag
         this.$el.querySelectorAll(".vti__flag")[0].className = "vti__flag au"
         this.lead.contactNumber = "+61"
      },

      disableFormButton() {

         if (this.v$.$invalid || this.v$.$error ||
            this.lead.leadType == '' || this.lead.priority == '' || this.lead.products == [] || this.lead.products.length == 0) {
            return true
         }

         return false
      },

      async createLead() {

         if (this.lead.contactNumber.trim().length < 5) {
            this.lead.contactNumber = ''
         }

         let lead = {
            organisation_name: this.lead.organisation,
            first_name: this.lead.firstName,
            last_name: this.lead.lastName,
            email: this.lead.email,
            phone_number: this.lead.contactNumber,
            abn: this.lead.abn,
            is_charity: this.lead.isCharity,
            lead_type_id: this.lead.leadType,
            lead_priority_id: this.lead.priority,
            marketing_channel_id: this.lead.marketing_channel,
            lead_notes: [{
               note_type: "initial_note_internal",
               note_details: this.lead.enquiryDetails
            }],
            country_code: this.lead.selectedCountry.code
         }

         lead.products = []
         this.lead.products.forEach((p) => {
            lead.products.push({
               product_id: p.id.toString()
            })
         })

         //console.log(lead)

         await this.$axios_internal.post("internal/leads", lead)
            .then((response) => {
               if (response.data.success) {
                  this.$toast.success(response.data.message)

                  this.resetForm();

               } else {
                  this.$toast.error(response.data.message)
               }
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })

         this.resetForm();

      },

      //added marketing channel async function
      async getMarketingChannelList() {
         await this.$axios_internal.get("internal/categories?name=MarketingChannel")
            .then((response) => {
               this.marketingChannels = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLeadTypeList() {
         await this.$axios_internal.get("internal/categories?name=LeadType")
            .then((response) => {
               this.leadTypes = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getLeadPriorityList() {
         await this.$axios_internal.get("internal/categories?name=LeadPriority")
            .then((response) => {
               this.leadPriorityTypes = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      async getProductList() {
         await this.$axios_internal.get("internal/categories?name=Products")
            .then((response) => {
               this.productList = response.data.category.subcategories
            })
            .catch((error) => {
               this.$toast.error(error.response.data.message)
            })
      },

      login() {
         this.$store.dispatch('auth/redirectToLogin');
      }

   },
};

</script>

<style scoped>
.btn {
   width: 7rem;
}

/* Fix for the blue hover and "Press enter to select" background */
::v-deep .multiselect__option--highlight {
  background: #00a0c1 !important;
  color: #fff !important;
}

/* Target the pseudo-element with the blue background */
::v-deep .multiselect__option--highlight::after {
  background: #00a0c1 !important;
  color: #fff !important;
}
</style>